import { useCallback, useEffect, useRef } from 'react';

import { IInputFieldIdToValueMap } from '../types/form-filling-types';
import {
	clearFormFillDraft,
	loadFormFillDraft,
	saveFormFillDraft,
} from '../utils/form-fill-draft-storage';

type Args = {
	formId: string | undefined;
	viewOnlyMode: boolean;
	values: IInputFieldIdToValueMap;
	onHydrate: (values: IInputFieldIdToValueMap) => void;
};

export function useFormFillDraft({ formId, viewOnlyMode, values, onHydrate }: Args) {
	const hydratedForFormIdRef = useRef<string | null>(null);

	useEffect(() => {
		if (viewOnlyMode || !formId) return;
		if (hydratedForFormIdRef.current === formId) return;

		hydratedForFormIdRef.current = formId;
		const draft = loadFormFillDraft(formId);
		if (draft) onHydrate(draft);
	}, [formId, viewOnlyMode, onHydrate]);

	// Persist the draft, but throttle the localStorage write so typing doesn't
	// serialize the whole answer map on every keystroke. The inputs update their
	// own state synchronously, so nothing user-facing waits on this.
	useEffect(() => {
		if (viewOnlyMode || !formId) return;

		const timeoutId = setTimeout(() => saveFormFillDraft(formId, values), 250);
		return () => clearTimeout(timeoutId);
	}, [formId, viewOnlyMode, values]);

	const clearDraft = useCallback(() => {
		if (formId) clearFormFillDraft(formId);
	}, [formId]);

	return { clearDraft };
}
